import React, { useCallback, useMemo } from 'react';
import { Box, Inscription } from '@common-fe/common-fe';

import routes from '@/common/routes';
import { useGeneralInfoStore } from '@/modules/employee/Employee/PersonalInformation/GeneralInfo/stores';

import { LMNClaim } from '../LettersOfMedicalNecessity.types';

interface Props {
  claims?: LMNClaim[];
  onShowClaims?: () => void;
  claim?: LMNClaim;
}

export const LettersOfMedicalNecessityClaims: React.FC<Props> = ({ claims, onShowClaims, claim }) => {
  const { employeeId } = useGeneralInfoStore((state) => state.state);
  const normalizedClaims = useMemo(() => {
    if (claim) return [claim];
    return claims || [];
  }, [claims, claim]);

  const getClaimLink = useCallback(
    (claimId: string | number) => {
      return routes.TRANSACTION_DETAILS_CLAIM(employeeId, claimId);
    },
    [employeeId]
  );

  if (!normalizedClaims.length) {
    return null;
  }

  if (normalizedClaims.length === 1) {
    const currentClaim = normalizedClaims[0];

    if (!currentClaim?.claimId) {
      return null;
    }

    const formattedLink = getClaimLink(currentClaim.claimId);

    return (
      <Box flex>
        <a
          href={formattedLink}
          data-testid="lmn-single-claim_link"
          target="_blank"
          style={{ textDecoration: 'none', minWidth: 0 }}
          rel="noreferrer"
        >
          <Box direction="row" align="center" style={{ cursor: 'pointer' }}>
            <Inscription
              color="textAccent"
              textAlign="center"
              weight={400}
              size={claim ? '16px' : '14px'}
              truncate
            >
              {currentClaim.providerName || 'Provider'}
            </Inscription>
          </Box>
        </a>
      </Box>
    );
  }

  return (
    <Box
      direction="row"
      align="center"
      style={{ cursor: 'pointer' }}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        if (onShowClaims) {
          onShowClaims();
        }
      }}
      data-testid="lmn-multiple-claims_button"
    >
      <Inscription color="textAccent" textAlign="center">
        Show Claims ({normalizedClaims.length})
      </Inscription>
    </Box>
  );
};
